'use client';

import { useEffect, useState } from 'react';
import { ArrowPathIcon, ChartBarIcon, InformationCircleIcon, MapPinIcon } from '@heroicons/react/24/outline';

import { Button, IconButton } from '@/app/Components/Button';
import Modal from '@/app/Components/Modal';
import { OperationalEvidence, OperationalState, OperationalStatus } from '@/app/Components/OperationalPage';
import { getScheduleScoreBreakdown, type ScheduleScoreBreakdown } from '@/app/services/scheduleGenerator';
import type { GeneratedSchedule, NoScheduleReason, Preferences, ScheduledCourse } from '@/app/types/interfaces';

function ScoreRow({ label, value, maximum, signed = false }: { label: string; value: number; maximum?: number; signed?: boolean }) {
  const formatted = `${signed && value > 0 ? '+' : ''}${Math.round(value)}`;
  return <div className="flex items-center justify-between gap-4 text-sm"><dt className="text-[var(--ss-text-muted)]">{label}</dt><dd className="font-semibold tabular-nums text-[var(--ss-text)]">{formatted}{maximum !== undefined ? `/${Math.round(maximum)}` : ''}</dd></div>;
}

function ScoreDetails({ scheduleNumber, breakdown }: { scheduleNumber: number; breakdown: ScheduleScoreBreakdown | null }) {
  if (!breakdown) return <OperationalState kind="empty" title="Scoring details unavailable" description="Set schedule preferences to calculate a weighted score breakdown." />;
  const cappedPenalty = Math.min(25, breakdown.logisticsPenalty);

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-end justify-between gap-4">
          <div><p className="text-sm text-[var(--ss-text-muted)]">Schedule {scheduleNumber}</p><p className="mt-1 text-3xl font-bold tabular-nums text-[var(--ss-text)]">{breakdown.total}/100</p></div>
          <OperationalStatus tone={breakdown.total >= 80 ? 'positive' : breakdown.total >= 60 ? 'warning' : 'critical'}>{breakdown.total >= 80 ? 'Strong fit' : breakdown.total >= 60 ? 'Good fit' : 'Needs review'}</OperationalStatus>
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-[var(--ss-surface-subtle)]" aria-label={`Score ${breakdown.total} out of 100`}><div className="h-full rounded-full bg-[var(--ss-positive)]" style={{ width: `${breakdown.total}%` }} /></div>
      </div>
      <dl className="space-y-3 rounded-card bg-[var(--ss-surface-subtle)] p-4">
        <ScoreRow label="Professor rating" value={breakdown.weightedProfessor} maximum={breakdown.weights.professor * 100} />
        <ScoreRow label="Time preference" value={breakdown.weightedTime} maximum={breakdown.weights.times * 100} />
        <ScoreRow label="Campus preference" value={breakdown.weightedCampus} maximum={breakdown.weights.campus * 100} />
        <ScoreRow label="Logistics penalty" value={-cappedPenalty} maximum={25} />
        <ScoreRow label="Friend bonus" value={breakdown.friendBonus} signed />
      </dl>
      <p className="text-xs leading-5 text-[var(--ss-text-muted)]">The three preference scores form a {Math.round(breakdown.baseScore)}-point base. Logistics and friend overlap are then applied before the result is limited to 0–100.</p>
    </div>
  );
}

export default function ScheduleGenerationsPanel({
  generatedSchedules,
  currentScheduleIndex,
  totalSchedulesGenerated,
  onScheduleSelect,
  onSortChange,
  onRegenerate,
  canRegenerate,
  generationNotice,
  noScheduleReasons,
  onOpenNoScheduleModal,
  preferences,
  friendSchedulesForScoring,
}: {
  generatedSchedules: GeneratedSchedule[];
  currentScheduleIndex: number;
  totalSchedulesGenerated?: number;
  onScheduleSelect: (index: number) => void;
  onSortChange: (key: 'score_desc' | 'score_asc') => void;
  onRegenerate?: () => void;
  canRegenerate?: boolean;
  generationNotice?: string;
  noScheduleReasons?: NoScheduleReason[];
  onOpenNoScheduleModal?: () => void;
  preferences?: Preferences | null;
  friendSchedulesForScoring?: ScheduledCourse[][];
}) {
  const [scoreSelection, setScoreSelection] = useState<{
    options: GeneratedSchedule[]; option: GeneratedSchedule; index: number;
  } | null>(null);
  // An index alone can point to a different option after an async replacement.
  // Fence during render so a missing row is never dereferenced before cleanup.
  const activeScoreSelection = scoreSelection && scoreSelection.options === generatedSchedules
    && generatedSchedules[scoreSelection.index] === scoreSelection.option ? scoreSelection : null;
  useEffect(() => {
    if (scoreSelection && !activeScoreSelection) setScoreSelection(null);
  }, [scoreSelection, activeScoreSelection]);
  const scoreDetails = !activeScoreSelection || !preferences
    ? null
    : getScheduleScoreBreakdown(activeScoreSelection.option.courses, preferences, friendSchedulesForScoring);

  return (
    <section className="space-y-4" aria-labelledby="all-generations-title">
      <div className="flex flex-col gap-4 border-b border-[var(--ss-border)] pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 id="all-generations-title" className="text-lg font-bold text-[var(--ss-text)]">Generated schedules</h2>
          <p className="mt-1 text-sm text-[var(--ss-text-muted)]">Showing the top {generatedSchedules.length}{totalSchedulesGenerated && totalSchedulesGenerated !== generatedSchedules.length ? ` of ${totalSchedulesGenerated}` : ''} options ranked by your preferences.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs font-semibold text-[var(--ss-text-muted)]"><span className="mb-1 block">Sort</span><select aria-label="Sort generated schedules" className="ss-field min-w-40" defaultValue="score_desc" onChange={(event) => onSortChange(event.target.value as 'score_desc' | 'score_asc')}><option value="score_desc">Best score</option><option value="score_asc">Lowest score</option></select></label>
          {canRegenerate ? <Button variant="secondary" onClick={onRegenerate}><ArrowPathIcon className="h-4 w-4" />Regenerate</Button> : null}
        </div>
      </div>

      {generationNotice ? <OperationalEvidence tone="warning">{generationNotice}</OperationalEvidence> : null}

      {generatedSchedules.length ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {generatedSchedules.map((schedule, index) => {
            const active = index === currentScheduleIndex;
            const campusCount = new Set(schedule.courses.map((course) => course.campus).filter(Boolean)).size;
            return (
              <article key={index} aria-current={active ? 'true' : undefined} className={`overflow-hidden rounded-card border transition-colors focus-within:ring-2 focus-within:ring-[var(--ss-focus)] motion-reduce:transition-none ${active ? 'border-[var(--ss-focus)] bg-[var(--ss-brand-bg)]' : 'border-[var(--ss-border)] bg-[var(--ss-surface)] hover:border-[var(--ss-focus)]'}`}>
                <button type="button" onClick={() => onScheduleSelect(index)} className="block w-full p-4 pb-2 text-left focus-visible:outline-none" aria-label={`Open schedule ${index + 1}, score ${schedule.score} out of 100`}>
                  <div className="flex items-start justify-between gap-3">
                    <div><h3 className="font-semibold text-[var(--ss-text)]">Schedule {index + 1}</h3><p className="mt-1 text-sm text-[var(--ss-text-muted)]">{schedule.courses.length} courses</p></div>
                    <OperationalStatus tone={schedule.score >= 80 ? 'positive' : schedule.score >= 60 ? 'warning' : 'critical'}>{schedule.score}/100</OperationalStatus>
                  </div>
                  <div className="mt-4 space-y-2">
                    {schedule.courses.map((course) => <div key={course.id} className="flex items-center justify-between gap-3 rounded-control bg-[var(--ss-surface-subtle)] px-3 py-2 text-sm"><span className="truncate font-medium text-[var(--ss-text)]">{course.name}</span><span className="shrink-0 text-xs text-[var(--ss-text-muted)]">Sec {course.section}</span></div>)}
                  </div>
                </button>
                <div className="flex items-center justify-between gap-3 px-4 pb-3 pt-1">
                  <p className="inline-flex items-center gap-1 text-xs text-[var(--ss-text-muted)]"><MapPinIcon className="h-4 w-4" />{campusCount} campus{campusCount === 1 ? '' : 'es'}</p>
                  <IconButton label={`Show scoring details for schedule ${index + 1}`} variant="quiet" onClick={() => setScoreSelection({ options: generatedSchedules, option: schedule, index })}><InformationCircleIcon className="h-5 w-5" /></IconButton>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <OperationalState kind="empty" title="No generated schedules" description={noScheduleReasons?.[0]?.message || 'Adjust courses or preferences, then generate schedule options.'} action={onOpenNoScheduleModal ? <Button variant="secondary" onClick={onOpenNoScheduleModal}><ChartBarIcon className="h-4 w-4" />Review generation details</Button> : undefined} />
      )}

      <Modal isOpen={activeScoreSelection !== null} onClose={() => setScoreSelection(null)} title="Scoring details" size="sm">
        {activeScoreSelection ? <ScoreDetails scheduleNumber={activeScoreSelection.index + 1} breakdown={scoreDetails} /> : null}
      </Modal>
    </section>
  );
}
